import pino from 'pino';

/*
 * Vendor API clients attach credentials to axios requests (headers, `auth`,
 * body, query params), and a logged AxiosError carries the whole request in
 * `config` / `request`. Only the parts of the request useful for debugging
 * are kept, with credentials removed.
 */

const REDACTED = '[REDACTED]';

// Headers not on this list are dropped, so a new credential header can't leak
const SAFE_HEADERS = [
  'accept',
  'content-type',
  'content-length',
  'user-agent',
  'netoapi_action',
];

// Credential-style body field names. A generic `_key` match would
// also hide record ids like `doc_key` / `offer_key`.
const SENSITIVE_FIELD =
  /authorization|(api|app|consumer|access|private|encryption|netoapi|neto|woo|mapbox)[-_]?key|token|secret|password|cookie|hmac|signature/i;

const isPlainObject = (value: unknown) =>
  value !== null &&
  typeof value === 'object' &&
  [Object.prototype, null].includes(Object.getPrototypeOf(value));

// Buffers, streams, FormData etc. are returned as-is, so a Buffer isn't
// logged as one key per byte
const redactFields = (value: any): any => {
  if (Array.isArray(value)) {
    return value.map(redactFields);
  }

  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [
        key,
        SENSITIVE_FIELD.test(key) ? REDACTED : redactFields(item),
      ]),
    );
  }

  return value;
};

// axios has already serialised a JSON request body to a string by the time it
// errors. Any other body (form-encoded, XML, Buffer, stream) can't be checked
// for credentials, e.g. an OAuth `client_secret` form, so it isn't logged.
const redactBody = (data: unknown) => {
  if (data === undefined || data === null) {
    return data;
  }

  if (typeof data !== 'string') {
    return Array.isArray(data) || isPlainObject(data)
      ? redactFields(data)
      : REDACTED;
  }

  try {
    return JSON.stringify(redactFields(JSON.parse(data)));
  } catch {
    return REDACTED;
  }
};

// The whole query string goes, as vendors put credentials there (e.g.
// WooCommerce `consumer_key`) under names we can't predict
const redactQuery = (url: string | undefined) =>
  url?.replace(/\?.*$/, `?${REDACTED}`);

// `https://user:pass@host` carries credentials in the URL's userinfo
const redactUserinfo = (url: string | undefined) =>
  url?.replace(/^((?:[a-z][a-z0-9+.-]*:)?\/\/)[^/?#]*@/i, `$1${REDACTED}@`);

const safeHeaders = (headers: Record<string, unknown> = {}) =>
  Object.fromEntries(
    Object.entries(headers).filter(([name]) =>
      SAFE_HEADERS.includes(name.toLowerCase()),
    ),
  );

const HEADER_FIELDS = ['headers', 'multiValueHeaders'];
const MAX_DEPTH = 8;

const redactLogValue = (
  value: any,
  depth: number,
  seen: WeakSet<object>,
): any => {
  if (Array.isArray(value) || isPlainObject(value)) {
    if (seen.has(value) || depth > MAX_DEPTH) {
      return '[Truncated]';
    }
    seen.add(value);
  }

  if (Array.isArray(value)) {
    return value.map((item) => redactLogValue(item, depth + 1, seen));
  }

  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => {
        if (SENSITIVE_FIELD.test(key)) {
          return [key, REDACTED];
        }

        // Request headers (e.g. a whole API Gateway event) are allow-listed
        // as any new credential header would otherwise leak
        if (HEADER_FIELDS.includes(key) && isPlainObject(item)) {
          return [key, safeHeaders(item as Record<string, unknown>)];
        }

        return [key, redactLogValue(item, depth + 1, seen)];
      }),
    );
  }

  return value;
};

/**
 * Applied to every object passed to the Logger, so logging a whole request,
 * event or body can't put credentials in the logs. Errors, buffers etc. are
 * left to their own serializers.
 */
export const redactLogObject = (obj: Record<string, unknown>) =>
  redactLogValue(obj, 0, new WeakSet());

const serializeConfig = (config: any) =>
  config && {
    method: config.method,
    baseURL: redactQuery(redactUserinfo(config.baseURL)),
    url: redactQuery(redactUserinfo(config.url)),
    // Appended to the query string by axios
    params: config.params && REDACTED,
    headers: safeHeaders(config.headers),
    data: redactBody(config.data),
  };

// Same check pino's standard serializer uses
const isErrorLike = (value: any) => typeof value?.message === 'string';

/**
 * pino's standard error serializer, except AxiosErrors keep only a safe
 * subset of their request and response, including when nested in another
 * error.
 */
export const serializeError = (
  value: any,
  seen: WeakSet<object> = new WeakSet(),
): any => {
  // An error already being serialised further up. pino's own output for it
  // would include any AxiosError it holds unstripped.
  if (seen.has(value)) {
    return '[Circular]';
  }

  const serialized: any = pino.stdSerializers.err(value);

  if (!isErrorLike(value)) {
    return serialized;
  }

  seen.add(value);

  // The standard serializer serialises nested errors itself, which would keep
  // a nested AxiosError's full request. `cause` is skipped as pino only keeps
  // its message and stack.
  Object.keys(value).forEach((key) => {
    if (key !== 'cause' && isErrorLike(value[key])) {
      serialized[key] = serializeError(value[key], seen);
    }
  });

  if (Array.isArray(value.errors)) {
    serialized.aggregateErrors = value.errors.map((error: any) =>
      serializeError(error, seen),
    );
    // pino also copies the raw array when it's enumerable, which stringifies
    // each AxiosError via its toJSON; `aggregateErrors` already holds them
    delete serialized.errors;
  }

  if (value.isAxiosError !== true) {
    return serialized;
  }

  // `request` is the raw http.ClientRequest, which holds the serialised
  // request headers. `toJSON` would put the full config back when stringified.
  const { request, toJSON, ...rest } = serialized as any;

  return {
    ...rest,
    config: serializeConfig(value.config),
    // Response headers are left out as they can carry `set-cookie` sessions
    response: value.response && {
      status: value.response.status,
      data: redactBody(value.response.data),
    },
  };
};

/**
 * RT-17925: an AxiosError thrown out of a Lambda handler is printed by the
 * runtime with its whole `config` and `request`, which carry credentials
 * (e.g. an `x-api-key` header). Strips them in place, keeping the fields
 * callers read (`response.status` / `response.data`, `message`, `code`).
 */
export const scrubAxiosError = <T>(error: T): T => {
  const err = error as any;

  if (err?.isAxiosError !== true) {
    return error;
  }

  try {
    err.config = serializeConfig(err.config);
    delete err.request;

    if (err.response) {
      err.response = {
        status: err.response.status,
        statusText: err.response.statusText,
        data: redactBody(err.response.data),
      };
    }
  } catch {
    // never mask the original error because scrubbing failed
  }

  return error;
};
